import { Checkbox, Icon, IconButton } from '@smartgift/frontend-toolkit';
import { useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Table, TableName } from '@hero/molecules';
import { recognitionServices, useGetRecognitionRecipientList } from '@hero/services';
import { cleanObjectEmptyFields } from '@hero/utils';

import { RecipientSelectionHead } from './RecipientSelectionHead';

const BasicRecipientTable = ({
  disabledRows = [],
  selectedRows,
  onChangeSelectedRows,
  recognitionCode,
  ids,
  actionButtons = {},
  onRemoveSelectedRows,
  components = {}
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [globalFilters, setGlobalFilters] = useState({});
  const [rowSelection, setRowSelection] = useState(selectedRows || {});

  const { data: recipients, isFetching } = useGetRecognitionRecipientList(
    {
      code: recognitionCode,
      filters: {
        searchTerm: globalFilters.searchTerm,
        ids
      },
      pagination: {
        page: pageIndex + 1,
        limit: pageSize
      }
    },
    {
      enabled: ids?.length > 0
    }
  );

  function firstNameCell({
    row: {
      original: { firstName, lastName }
    }
  }) {
    return <TableName firstName={firstName} lastName={lastName} />;
  }

  const columns = React.useMemo(
    () => [
      {
        id: 'select',
        maxSize: 30,
        header: ({ table }) => {
          return (
            <Checkbox
              {...{
                key: table.getState().pagination?.pageIndex,
                checked: table.getIsAllPageRowsSelected(),
                indeterminate: table.getIsSomePageRowsSelected(),
                onChange: table.getToggleAllPageRowsSelectedHandler()
              }}
            />
          );
        },
        cell: ({ row, table }) => {
          const { disabledRows } = table.getState();
          return (
            <Checkbox
              {...{
                checked: disabledRows?.[row.original?.id] || row.getIsSelected(),
                disabled: disabledRows?.[row.original?.id],
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler()
              }}
            />
          );
        }
      },
      {
        header: t('name'),
        size: 200,
        accessorKey: 'firstName',
        cell: firstNameCell
      },
      {
        id: 'email',
        header: t('email'),
        accessorKey: 'email'
      },
      {
        id: 'tags',
        header: 'Tag',
        accessorFn: originalRow => {
          return originalRow?.tags?.map(tag => ({
            label: tag.name,
            value: tag.id
          }));
        },
        type: 'tags'
      },
      ...(actionButtons?.delete
        ? [
            {
              id: 'actions',
              header: '',
              size: 50,
              cell: ({ row }) => {
                return (
                  <IconButton
                    variant="text"
                    color="neutral"
                    onClick={() =>
                      actionButtons.delete.onClick(row.original, () => {
                        setRowSelection({});
                      })
                    }
                  >
                    <Icon name="delete" size="md" />
                  </IconButton>
                );
              }
            }
          ]
        : [])
    ],
    []
  );

  async function onToggleSelectAll(table) {
    const searchTerm = globalFilters.searchTerm;

    const data = await queryClient.fetchQuery(
      ['recog-recipients', recognitionCode, searchTerm, 'contact.id', false],
      () => {
        return recognitionServices.recogRecipientsList(
          recognitionCode,
          cleanObjectEmptyFields({
            filter: {
              searchTerm
            },
            options: {
              select: 'contact.id',
              pagination: false
            }
          })
        );
      }
    );

    if (data?.docs?.length)
      table.setRowSelection(
        data.docs.reduce((result, doc) => ({ ...result, [doc.contact.id]: true }), {})
      );
  }

  React.useEffect(() => {
    if (onChangeSelectedRows) onChangeSelectedRows(rowSelection);
  }, [rowSelection]);

  return (
    <Table
      loading={isFetching}
      columns={columns}
      data={recipients?.docs || []}
      globalFilters={globalFilters}
      pagination={{
        pageIndex,
        pageSize,
        total: recipients?.totalDocs
      }}
      state={{
        disabledRows,
        onRemoveSelectedRows: () => {
          onRemoveSelectedRows({
            onSuccess: () => {
              setRowSelection({});
            }
          });
        },
        onToggleSelectAll
      }}
      getRowId={row => row?.contact?.id}
      rowSelection={rowSelection}
      setRowSelection={setRowSelection}
      manualPagination
      setGlobalFilters={setGlobalFilters}
      setPagination={setPagination}
      pageCount={recipients?.totalPages}
      enabled={{ filter: false, header: true }}
      rowHeight={52}
      filteredWithText={t('filteredWithText', {
        filteredContactCount: recipients?.totalDocs || '0'
      })}
      filterProps={{
        placeholder: t('filterByType', { type: t('tags') }),
        searchPlaceholder: t('searchTagOnFilterPlaceholder')
      }}
      components={{
        SelectionHead: RecipientSelectionHead,
        ...components
      }}
    />
  );
};

BasicRecipientTable.propTypes = {
  disabledRows: PropTypes.array,
  selectedRows: PropTypes.object,
  onChangeSelectedRows: PropTypes.func,
  recognitionCode: PropTypes.string,
  ids: PropTypes.array,
  actionButtons: PropTypes.shape({ delete: PropTypes.node }),
  onRemoveSelectedRows: PropTypes.func,
  components: PropTypes.node
};

export { BasicRecipientTable };
